// What a business sells: up to three kinds, the first ticked is the main one
// (docs/plans/business-kinds.md, boards KindsSignUp and KindsSettings). Used at sign-up
// and by "Change kinds" in Settings → Business.
import { Badge, cx } from '@ims/ui'
import {
  ALL_IN_ONE,
  BUSINESS_TYPES,
  GROUP_BY_ID,
  MAX_KINDS,
  defaultGroups,
  type BusinessType,
} from '../lib/catalog.ts'

/** The kinds after a tap on one: a restaurant is alone, and three is the most. */
export function toggleKind(kinds: BusinessType[], kind: BusinessType): BusinessType[] {
  if (kinds.includes(kind)) return kinds.filter((k) => k !== kind)
  if (kind === 'restaurant') return ['restaurant']
  const rest = kinds.filter((k) => k !== 'restaurant')
  return rest.length >= MAX_KINDS ? rest : [...rest, kind]
}

const sameKinds = (a: BusinessType[], b: BusinessType[]) =>
  a.length === b.length && a.every((k) => b.includes(k))

/** The words for a list of kinds: "Agrovet, Pharmacy and Mobile & electronics". */
export function kindsLabel(kinds: BusinessType[]): string {
  const labels = kinds.map((k) => BUSINESS_TYPES.find((t) => t.id === k)?.label ?? k)
  return labels.length > 1 ? `${labels.slice(0, -1).join(', ')} and ${labels.at(-1)}` : labels[0]!
}

const tile =
  'relative cursor-pointer rounded-xl border p-2.5 text-left text-sm transition-colors has-focus-visible:ring-2 has-focus-visible:ring-primary/30'

export function KindPicker({
  kinds,
  onChange,
  disabled = false,
}: {
  kinds: BusinessType[]
  onChange: (kinds: BusinessType[]) => void
  disabled?: boolean
}) {
  const full = kinds.length >= MAX_KINDS
  const allInOne = sameKinds(kinds, ALL_IN_ONE)
  const fields = kinds.length
    ? defaultGroups(kinds)
        .map((g) => GROUP_BY_ID[g])
        .filter((g) => g.available)
        .map((g) => g.label)
    : []
  return (
    <div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {BUSINESS_TYPES.map((t) => {
          const at = kinds.indexOf(t.id)
          const picked = at >= 0
          const locked = disabled || (!picked && full && t.id !== 'restaurant')
          return (
            <label
              key={t.id}
              className={cx(
                tile,
                picked ? 'border-primary bg-primary-light' : 'border-border',
                locked ? 'cursor-not-allowed opacity-60' : !picked && 'hover:border-primary/40',
              )}
            >
              <input
                type="checkbox"
                name="business_kind"
                value={t.id}
                checked={picked}
                disabled={locked}
                onChange={() => onChange(toggleKind(kinds, t.id))}
                className="sr-only"
              />
              <span className="flex items-start gap-1.5">
                <span className="min-w-0 flex-1 font-medium">{t.label}</span>
                {at === 0 && (
                  <Badge tone="primary" className="shrink-0 font-semibold">
                    Main
                  </Badge>
                )}
              </span>
              <span className="block text-xs text-muted max-sm:hidden">{t.example}</span>
            </label>
          )
        })}
        <button
          type="button"
          aria-pressed={allInOne}
          disabled={disabled}
          onClick={() => onChange(allInOne ? [] : [...ALL_IN_ONE])}
          className={cx(
            tile,
            'border-dashed',
            allInOne ? 'border-primary bg-primary-light' : 'border-border hover:border-primary/40',
          )}
        >
          <span className="block font-medium">All in one</span>
          <span className="block text-xs text-muted max-sm:hidden">
            Grocery, hardware and cosmetics together
          </span>
        </button>
      </div>
      <p className="mt-2 text-xs text-muted" aria-live="polite">
        {kinds.length === 0
          ? 'Nothing ticked makes it an “Other shop”.'
          : full
            ? `${kinds.length} of ${MAX_KINDS}. Take one off to pick another.`
            : kinds[0] === 'restaurant'
              ? 'A restaurant is its own choice: tables, kitchen tickets and the menu.'
              : null}
        {fields.length > 0 && (
          <span className="block">
            <span className="font-medium text-ink">Product fields that come on:</span>{' '}
            {fields.join(', ')}.
          </span>
        )}
      </p>
    </div>
  )
}
